import React, { useState, useEffect, useCallback } from "react";
import "../page/productCategory.css";

import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import { Constant } from "../../../helper/constant";
import ProductCategoryHeader from "../component/productCategoryHeader";
import ProductCategoryTable from "../component/productCategoryTable";
import ModalAddProductCategory from "../component/modalAddProductCategory";
import { useNavigate } from "react-router-dom";

const ProductCategory = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModalAddProductCategory, setIsOpenModalAddProductCategory] =
    useState(false);
  const [page, setPage] = useState(0);

  const [selectedProductCategoryData, setSelectedProductCategoryData] =
    useState({
      product_category_id: "",
      product_category_name: "",
    });
  const [listProductCategoryData, setListProductCategoryData] = useState(null);
  const [
    isOpenModalDeleteProductCategory,
    setIsOpenModalDeleteProductCategory,
  ] = useState(false);
  const [idProductCategoryToDelete, setIdProductCategoryToDelete] =
    useState("");

  // =====================
  // Function Definition
  // =====================

  const addNewProductCategory = () => {
    resetSelectedProductCategoryDataValue();
    setIsOpenModalAddProductCategory(true);
  };

  const onEditProductCategory = (productCategoryData) => {
    setSelectedProductCategoryData(productCategoryData);
    setIsOpenModalAddProductCategory(true);
  };

  const onDeleteProductCategory = (productCategoryData) => {
    setIsOpenModalDeleteProductCategory(true);
    setIdProductCategoryToDelete(productCategoryData.product_category_id);
  };

  const onDeleteProductCategoryConfirmed = () => {
    setIsOpenModalDeleteProductCategory(false);
    deleteProductCategory();
  };

  const onCloseModalDeleteProductCategory = () => {
    setIdProductCategoryToDelete("");
    setIsOpenModalDeleteProductCategory(false);
  };

  const onCloseModal = () => {
    resetSelectedProductCategoryDataValue();
    setIsOpenModalAddProductCategory(false);
  };

  const onSubmitNewProductCategory = (productCategoryData, isEditData) => {
    setIsOpenModalAddProductCategory(false);
    if (isEditData) {
      editProductCategory(productCategoryData);
    } else {
      createNewProductCategory(productCategoryData);
    }
  };

  const resetSelectedProductCategoryDataValue = () => {
    setSelectedProductCategoryData({
      product_category_id: "",
      product_category_name: "",
    });
  };

  // =====================
  // Request Method
  // =====================

  const getProductCategoryData = useCallback(async () => {
    try {
      setIsLoading(true);
      localStorage.setItem("searchValue", "");
      const response = await fetch(Constant.BASE_API_URL + "/kategori-produk", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const productCategory = data.product_category;
      if (!productCategory) {
        setListProductCategoryData([]);
      }
      setListProductCategoryData(productCategory);
    } catch (error) {
      setListProductCategoryData([]);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const createNewProductCategory = async (productCategoryData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/kategori-produk", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          product_category_name: productCategoryData.product_category_name,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getProductCategoryData();
    }
  };

  const editProductCategory = async (productCategoryData) => {
    try {
      setIsLoading(true);
      const productCategoryId = productCategoryData.product_category_name;
      const response = await fetch(
        Constant.BASE_API_URL + "/kategori-produk/" + productCategoryId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            product_category_name: productCategoryData.product_category_name,
          }),
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getProductCategoryData();
    }
  };

  const deleteProductCategory = async () => {
    try {
      setIsLoading(true);
      const productCategoryId = idProductCategoryToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/kategori-produk/" + productCategoryId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdProductCategoryToDelete("");
      getProductCategoryData();
    }
  };

  const searchProductCategory = (searchText) => {
    if (searchText.length > 3) {
      getProductCategorySearchData(searchText, false, false, "", "");
    } else if (searchText.length === 0) {
      getProductCategoryData();
    }
  };

  const getProductCategorySearchData = useCallback(
    async (keyword, isNextPage, isPreviousPage, nextCursor, prevCursor) => {
      try {
        setIsLoading(true);

        let state = "";
        let cursorId = "";

        if (isNextPage) {
          state = "next_page";
          cursorId = nextCursor;
        } else if (isPreviousPage) {
          state = "previous_page";
          cursorId = prevCursor;
        }

        let url = new URL(Constant.BASE_API_URL + "/kategori-produk/search");
        url.searchParams.append("keyword", keyword);
        if (state !== "") {
          url.searchParams.append("cursor", cursorId);
          url.searchParams.append("state", state);
        } else {
          setPage(0);
        }

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        });
        if (!response.ok) {
          alert("Gagal Menerima Data");
          if (response.status === 401 || response.status === 403) {
            localStorage.clear();
            return navigate("/login", { replace: true });
          }
        }
        const json = await response.json();
        const data = json.data;
        const productCategory = data.product_category;
        if (!productCategory) {
          setListProductCategoryData(null);
        } else {
          setListProductCategoryData(productCategory);
        }
      } catch (error) {
        setListProductCategoryData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    getProductCategoryData();
  }, [getProductCategoryData]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Produk Kategori</h1>
      <ProductCategoryHeader
        onAddProductCategoryClicked={addNewProductCategory}
        onSearchProductCategoryEdited={searchProductCategory}
      />
      <ProductCategoryTable
        productCategory={listProductCategoryData}
        onEditProductCategoryClicked={onEditProductCategory}
        onDeleteProductCategoryClicked={onDeleteProductCategory}
      />
      <ModalAddProductCategory
        isShowModal={isOpenModalAddProductCategory}
        onCloseModal={onCloseModal}
        onSubmitData={onSubmitNewProductCategory}
        productCategoryObject={selectedProductCategoryData}
        key={Date.now()}
      />
      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteProductCategory}
        onCloseModal={onCloseModalDeleteProductCategory}
        onDeleteConfirmed={onDeleteProductCategoryConfirmed}
        idToDelete={idProductCategoryToDelete}
      />
    </div>
  );
};

export default ProductCategory;
